"use client";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import {
  dateFormats,
  formatDate,
  getShortDayNames,
} from "@/utils/dateFormatter";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { format } from "date-fns";
import { useLocale } from "next-intl";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { DayPicker, getDefaultClassNames } from "react-day-picker";
import { ar, az, de, enUS, ru, tr } from "react-day-picker/locale";
import DayButton from "./DayButton";

const localeMap = {
  tr: tr,
  en: enUS,
  de: de,
  ar: ar,
  ru: ru,
  az: az
}

const DatePickerCalendarContent = memo(
  ({
    value,
    handleDateSelect,
    defaultMonth,
    pickUpDate,
    dropOffDate,
    publicHolidays,
  }) => {
    const isMobile = useIsMobile();
    const locale = useLocale();
    const t = useTranslationDetector("carSearch");
    const defaultClassNames = getDefaultClassNames();

    const defaultMonthValue = useMemo(() => {
      if (isMobile) {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth());
      } else {
        const date = new Date(defaultMonth);
        return new Date(date.getFullYear(), date.getMonth());
      }
    }, [isMobile, defaultMonth]);

    const memoizedDayButton = useCallback(
      (props) => (
        <DayButton
          {...props}
          showDropOffToolTip={!dropOffDate ? t("addDropOffDate") : false}
          isHoliday={publicHolidays.some(
            (holiday) =>
              holiday.date ===
              formatDate(props.day.date, { format: "YYYY-MM-DD" }),
          )}
        />
      ),
      [publicHolidays, dropOffDate, t],
    );

    const disabled = useMemo(() => {
      return { before: new Date() };
    }, [pickUpDate, dropOffDate]);

    useEffect(() => {
      setTimeout(() => {
        const container = document.querySelector(".months-container");
        const selectedMonth = container?.querySelector(".selected-month");

        if (container && selectedMonth) {
          const selectedTop = selectedMonth.offsetTop;
          const containerHeight = container.clientHeight;
          const selectedHeight = selectedMonth.clientHeight;

          // Seçili elemanı container'ın ortasına getir
          const scrollTo =
            selectedTop - containerHeight / 2 + selectedHeight / 2;

          container.scrollTop = scrollTo;
        }
      }, 100);
    }, []);

    return (
      <div className="flex h-full flex-col gap-4 px-0 py-4 md:px-4">
        <div className="flex items-center justify-between gap-4 px-4 md:hidden md:px-0">
          <button
            className={clsx(
              "bg-primary-weaker flex h-14 w-1/2 flex-col items-start justify-center rounded-lg border-2 px-3 py-2",
              !pickUpDate
                ? "border-primary bg-primary-weaker"
                : "border-border bg-white",
            )}
          >
            <span className="text-xs">{t("pickUp")}</span>
            <span className="text-sm font-medium">
              {dateFormats.dayLongMonthShortDay(pickUpDate, locale)}
            </span>
          </button>
          <button
            className={clsx(
              "flex h-14 w-1/2 flex-col items-start justify-center rounded-lg border-2 px-3 py-2",
              !dropOffDate
                ? "border-primary bg-primary-weaker"
                : "border-border bg-white",
            )}
          >
            <span className="text-xs">{t("dropOff")}</span>
            <span className="text-sm font-medium">
              {dropOffDate ? dateFormats.dayLongMonthShortDay(dropOffDate) : ""}
            </span>
          </button>
        </div>

        <div className="border-primary-weak flex items-center justify-between border-b px-2 md:hidden">
          {getShortDayNames(locale).map((day) => (
            <span className="w-[55px] py-2 text-center" key={day}>
              {day}
            </span>
          ))}
        </div>

        <div className="months-container h-full overflow-scroll px-0 pb-4 md:overflow-visible md:pb-0">
          <DayPicker
            key={`daypicker-${isMobile ? "mobile" : "desktop"}`}
            formatters={{
              formatWeekdayName: (date, options) =>
                format(date, "ccc", options),
            }}
            locale={localeMap[locale] || tr}
            animate
            mode="range"
            selected={value}
            onSelect={handleDateSelect}
            // onDayMouseEnter={!isMobile ? handleDayMouseEnter : undefined}
            // onDayMouseLeave={!isMobile ? handleDayMouseLeave : undefined}
            // modifiers={!isMobile ? modifiers : {}}
            // modifiersStyles={{
            //   hoverRange: {
            //     backgroundColor: "#F2F2F9",
            //   },
            // }}
            numberOfMonths={isMobile ? 12 : 2}
            navLayout="around"
            hideWeekdays={isMobile ? true : false}
            // hideNavigation={isMobile ? true : false}
            defaultMonth={defaultMonthValue}
            disabled={disabled}
            classNames={{
              root: `${defaultClassNames.root} [&>*]:select-none`,
              months: `${defaultClassNames.months} justify-between !max-w-full`,
              month: `${defaultClassNames.month} w-full md:w-auto`,
              month_grid: `${defaultClassNames.month_grid} w-full`,
              caption_label: `${defaultClassNames.caption_label} font-semibold`,
              disabled: "text-foreground-weaker-subtext",
              today: "text-primary",
              day: "font-semibold w-[55px] h-[58px]",
              selected: "",
              weekday:
                "border-b border-primary-weak text-sm text-foreground-muted font-medium h-11",
              button_next:
                "absolute right-0 top-[13px] w-[9px] text-black cursor-pointer opacity-0 md:opacity-100",
              button_previous:
                "absolute left-0 top-[13px] w-[9px] text-black cursor-pointer opacity-0 md:opacity-100",
            }}
            components={{
              DayButton: memoizedDayButton,
              Month: (props) => {
                const currentMonthHolidays = publicHolidays.filter(
                  (holiday) => {
                    const holidayDate = new Date(holiday.date);
                    const calendarDate = props.calendarMonth.date;

                    return (
                      holidayDate.getFullYear() ===
                        calendarDate.getFullYear() &&
                      holidayDate.getMonth() === calendarDate.getMonth()
                    );
                  },
                );

                return (
                  <div
                    className={clsx(
                      "flex w-full flex-col items-center justify-between gap-2 md:w-auto",
                      props.calendarMonth.date.getFullYear() ===
                        pickUpDate.getFullYear() &&
                        props.calendarMonth.date.getMonth() ===
                          pickUpDate.getMonth() &&
                        "selected-month",
                    )}
                  >
                    <div className="w-full">{props.children}</div>
                    {currentMonthHolidays?.length > 0 && (
                      <div className="flex w-full flex-col items-start gap-1 pl-[14px] md:pb-0">
                        {currentMonthHolidays.map((holiday) => (
                          <div
                            className={clsx(
                              "flex w-full items-center justify-start gap-[9px]",
                              !holiday.title && "hidden",
                            )}
                            key={holiday.date}
                          >
                            <span className="bg-tertiary-emphasis h-[6px] w-[6px] rounded-full"></span>
                            <span className="text-foreground-muted inline-flex items-center gap-1 text-sm">
                              <span>
                                {dateFormats.dayShortMonth(
                                  holiday.date,
                                  locale,
                                )}
                              </span>
                              <span>{holiday.title}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              },
              NextMonthButton: (props) => {
                return (
                  <FontAwesomeIcon
                    icon={byPrefixAndName.far["chevron-right"]}
                    {...props}
                  />
                );
              },
              PreviousMonthButton: (props) => {
                return (
                  <FontAwesomeIcon
                    icon={byPrefixAndName.far["chevron-left"]}
                    {...props}
                  />
                );
              },
            }}
          />
        </div>
      </div>
    );
  },
);

DatePickerCalendarContent.displayName = "DatePickerCalendarContent";

export default DatePickerCalendarContent;
